import { Component, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiTranslationsHistoryService, TranslationsHistoryItem } from '../../../../shared/services/api-translationsHistory.service';
import { ApiTranslationsImportService, TranslationsImportFileError, UploadType } from '../../../../shared/services/api-translations-import.service';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { ApiSurvey } from '../../../../shared/services/api-organization.service';

@Component({
  selector: 'app-translations-import',
  templateUrl: './translations-import.component.html',
  styleUrls: ['./translations-import.component.css'],
  imports: [CommonModule, FormsModule, PageLoaderComponent, BreadcrumbComponent]
})
export class TranslationsImportComponent implements OnInit {
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  requesting = signal<boolean>(false);
  loadingHistory = signal<boolean>(false);
  surveyList: ApiSurvey[];
  organizationId: string = '';
  selectedUploadType: UploadType = 'SurveyTranslations';
  selectedFiles: File[] = [];
  fileError = signal<string | null>(null);
  importApiError = signal<TranslationsImportFileError | null>(null);
  copiedText = signal<string | null>(null);
  historyFilterType: string = '';
  private allHistoryItems: TranslationsHistoryItem[] = [];
  historyItems = signal<TranslationsHistoryItem[]>([]);
  totalCount = signal<number>(0);
  currentPage = signal<number>(1);
  historyPerPage: number = 10;
  readonly perPageOptions = [5, 10, 25, 50];

  get totalPages(): number {
    return Math.ceil(this.totalCount() / this.historyPerPage) || 1;
  }

  get canImport(): boolean {
    return this.canEdit() && this.selectedFiles.length > 0 && !this.requesting();
  }

  readonly canEdit = computed(() => this.userStore.isAdmin());

  constructor(
    private router: Router,
    public organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private translationsHistoryService: ApiTranslationsHistoryService,
    private translationsImportService: ApiTranslationsImportService) {
      this.surveyList = organizationStore.selectedOrganizationSurveys();
    }

  ngOnInit() {
    this.organizationId = this.organizationStore.selectedOrganizationId() ?? '';
    if (!this.organizationId) {
      this.router.navigate(['/organizations']);
      return;
    }
    this.loadAllHistory();
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedText.set(text);
      setTimeout(() => this.copiedText.set(null), 1500);
    });
  }

  getSurveyName(surveyId: string): string {
    if (!surveyId) return '—';
    return this.surveyList.find(s => s.id === surveyId)?.name ?? surveyId;
  }

  getFileName(relativePath: string): string {
    if (!relativePath) return '—';
    return relativePath.split(/[\\/]/).pop() ?? relativePath;
  }

  onFileChange(event: Event): void {
    if (!this.canEdit()) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const allowed = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const allowedExt = ['.csv', '.xlsx', '.xls'];
    const files = Array.from(input.files);
    const invalid = files.filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      return !allowed.includes(f.type) && !allowedExt.includes(ext);
    });

    if (invalid.length > 0) {
      this.fileError.set(`Invalid file type: ${invalid.map(f => f.name).join(', ')}. Only CSV and Excel files are accepted.`);
      input.value = '';
      return;
    }

    this.fileError.set(null);

    // Accumulate across multiple browse actions; a re-picked file with the same name replaces the earlier one.
    const merged = [...this.selectedFiles];
    for (const file of files) {
      const existingIndex = merged.findIndex(f => f.name === file.name);
      if (existingIndex >= 0) merged[existingIndex] = file;
      else merged.push(file);
    }
    this.selectedFiles = merged;

    // Reset so picking the same file(s) again still fires a change event.
    input.value = '';
  }

  removeFile(index: number): void {
    if (!this.canEdit()) return;
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
  }

  requestImport(): void {
    if (!this.canImport) return;
    this.error.set(null);
    this.importApiError.set(null);
    this.requesting.set(true);
    this.translationsImportService.requestTranslationsImport(
      this.organizationId,
      this.selectedUploadType,
      this.selectedFiles
    ).subscribe({
      next: () => {
        this.requesting.set(false);
        this.selectedFiles = [];
        this.loadAllHistory();
      },
      error: (err) => {
        this.requesting.set(false);
        const body = err?.error as TranslationsImportFileError;
        if (body?.code && body?.description) {
          this.importApiError.set(body);
        } else {
          this.error.set(err?.message ?? 'Import request failed.');
        }
      }
    });
  }

  private applyFilterAndPage(): void {
    const filtered = this.allHistoryItems.filter(i =>
      (!this.historyFilterType || i.importType === this.historyFilterType)
    );
    this.totalCount.set(filtered.length);
    const start = (this.currentPage() - 1) * this.historyPerPage;
    this.historyItems.set(filtered.slice(start, start + this.historyPerPage));
  }

  loadAllHistory(): void {
    this.loadingHistory.set(true);
    this.translationsHistoryService.getTranslationsHistory(this.organizationId, null, 'Import', 1, 500).subscribe({
      next: (history) => {
        this.allHistoryItems = history.items;
        this.currentPage.set(1);
        this.applyFilterAndPage();
        this.loadingHistory.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load import history. | ' + err);
        this.loadingHistory.set(false);
      }
    });
  }

  onHistoryFilterChange(): void {
    this.currentPage.set(1);
    this.applyFilterAndPage();
  }

  onPerPageChange(): void {
    this.currentPage.set(1);
    this.applyFilterAndPage();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.applyFilterAndPage();
  }
}
